import { loadSite } from './site';

export type ThemeId = 'nailsly' | 'rose' | 'lavender' | 'peach' | 'navy' | 'forest';
export type ThemeGroup = 'Padrão' | 'Feminino' | 'Masculino';

export interface ThemeOption {
  id: ThemeId;
  name: string;
  group: ThemeGroup;
  description: string;
  swatch: { base: string; accent: string; surface: string };
  /** Gradiente usado nos cartões de escolha do tema (estilo iOS). */
  gradient: string;
  /** Cor da barra do navegador/PWA; acompanha o tema ativo. */
  metaColor: string;
}

/** Espelha SITE_THEMES em apps/api/src/modules/site/themes.ts. */
export const THEMES: ThemeOption[] = [
  {
    id: 'nailsly',
    name: 'AgendaPro',
    group: 'Padrão',
    description: 'Tinta profunda com acabamento âmbar.',
    swatch: { base: '#101a24', accent: '#a8762e', surface: '#eef2f6' },
    gradient: 'linear-gradient(135deg, #2b3d4d 0%, #101a24 58%, #0a1119 100%)',
    metaColor: '#101a24'
  },
  {
    id: 'rose',
    name: 'Rosé',
    group: 'Feminino',
    description: 'Rosa-choque com magenta elétrico.',
    swatch: { base: '#3a0b1f', accent: '#e0004a', surface: '#ffe6ee' },
    gradient: 'linear-gradient(135deg, #ff4d79 0%, #e0004a 55%, #8c0033 100%)',
    metaColor: '#3a0b1f'
  },
  {
    id: 'lavender',
    name: 'Lavanda',
    group: 'Feminino',
    description: 'Violeta elétrico com lilás luminoso.',
    swatch: { base: '#2a0e4a', accent: '#7c3aed', surface: '#f0e9ff' },
    gradient: 'linear-gradient(135deg, #b79cff 0%, #7c3aed 55%, #4c1d95 100%)',
    metaColor: '#2a0e4a'
  },
  {
    id: 'peach',
    name: 'Pêssego',
    group: 'Feminino',
    description: 'Coral vivo com terracota quente.',
    swatch: { base: '#3b1608', accent: '#d84315', surface: '#ffeadf' },
    gradient: 'linear-gradient(135deg, #ffa06b 0%, #ff5a2b 52%, #b23a10 100%)',
    metaColor: '#3b1608'
  },
  {
    id: 'navy',
    name: 'Naval',
    group: 'Masculino',
    description: 'Azul elétrico com marinho profundo.',
    swatch: { base: '#071b33', accent: '#1565c0', surface: '#e3effc' },
    gradient: 'linear-gradient(135deg, #6cb6ff 0%, #1565c0 55%, #0a2a4d 100%)',
    metaColor: '#071b33'
  },
  {
    id: 'forest',
    name: 'Floresta',
    group: 'Masculino',
    description: 'Esmeralda intensa com verde profundo.',
    swatch: { base: '#06251a', accent: '#087f5b', surface: '#e1f4ec' },
    gradient: 'linear-gradient(135deg, #4ade9c 0%, #0fa06f 55%, #065f46 100%)',
    metaColor: '#06251a'
  }
];

export const DEFAULT_THEME: ThemeId = 'nailsly';
/** Temas que o estabelecimento escolhe em Configurações: três femininos e dois masculinos. */
export const BUSINESS_THEMES: ThemeOption[] = THEMES.filter((theme) => theme.group !== 'Padrão');
export const THEME_STORAGE_KEY = 'nailsly.theme';
/** Cor da barra do navegador quando o visitante escolhe o modo escuro. */
export const DARK_META_COLOR = '#0b1219';

const THEME_IDS: ReadonlySet<string> = new Set(THEMES.map((theme) => theme.id));

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && THEME_IDS.has(value);
}

export function normalizeTheme(value: unknown): ThemeId {
  return isThemeId(value) ? value : DEFAULT_THEME;
}

export function currentTheme(): ThemeId {
  return normalizeTheme(document.documentElement.getAttribute('data-theme'));
}

export function themeOption(id: ThemeId): ThemeOption {
  return THEMES.find((theme) => theme.id === id) ?? THEMES[0];
}

function readStoredTheme(): ThemeId {
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

/**
 * Aplica o tema na raiz do documento. `persist: false` serve para prévia no
 * painel administrativo — só o tema salvo no servidor vale para os visitantes.
 */
export function applyTheme(value: unknown, persist = true): ThemeId {
  const id = normalizeTheme(value);
  const root = document.documentElement;
  if (id === DEFAULT_THEME) root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', id);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const dark = root.getAttribute('data-mode') === 'dark';
    meta.setAttribute('content', dark ? DARK_META_COLOR : themeOption(id).metaColor);
  }
  if (persist) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, id);
    } catch {
      /* navegação privada sem localStorage: o tema ainda vale nesta aba */
    }
  }
  return id;
}

/** Usa o último tema conhecido antes do React montar, evitando piscar o tema padrão. */
export function bootTheme(): ThemeId {
  return applyTheme(readStoredTheme(), false);
}

/** Sincroniza com o tema global salvo no servidor; sem rede, mantém o cache local. */
export async function syncTheme(): Promise<ThemeId> {
  const site = await loadSite();
  if (!isThemeId(site.theme)) return currentTheme();
  return applyTheme(site.theme);
}
